import {isEmpty} from 'lodash-es';
import React from 'react';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';
import {hasAdminPermission} from '@afriex/core/utils';

import {Button, PermissionsProvider, SpinningLoader} from '@/components';
import type {BusinessAllowedFeature} from '@/types/AfriexBusiness';
import {logRejection} from '@/utils/logRejection';

import {AllowedFeatureNode} from './AllowedFeatureNode';
import {defaultFields} from './defaultFields';
import {useAllowedFeatureUpdate} from './useAllowedFeatureUpdate';
import {hydrateAllowedFeatureTree} from './utils';

type Props = {
  businessId: string;
  initialAllowedFeatures?: BusinessAllowedFeature[];
};
export const AllowedFeatureForm = ({
  businessId,
  initialAllowedFeatures,
}: Props) => {
  const defaultFeatures = hydrateAllowedFeatureTree(
    defaultFields,
    initialAllowedFeatures ?? [],
  );
  const initialData = {
    tree: defaultFeatures,
  };
  const {dispatch, isLoading, handleSubmit, state} =
    useAllowedFeatureUpdate(initialData);

  const submitLabel = isEmpty(initialData) ? 'Submit' : 'Update';
  const isDisabled = isEmpty(state?.tree) || isLoading;
  // Every staff role can read the features; only the roles holding
  // BusinessFeaturesUpdate can change them.
  const canUpdate = hasAdminPermission(
    localStorage.getItem('admin-role') ?? undefined,
    AfriexAdminPermission.BusinessFeaturesUpdate,
  );
  const handleToggle = (key: string, isSelected: boolean) => {
    dispatch({
      type: isSelected ? 'SELECT_FEATURE' : 'UNSELECT_FEATURE',
      payload: {key},
    });
  };
  return (
    <React.Fragment>
      <div className="pt-3 px-2 overflow-y-hidden">
        {isLoading ? (
          <div className="flex w-full h-full justify-center items-center my-5">
            <SpinningLoader />
          </div>
        ) : (
          <form
            data-cy="business-details-allowed-features-form"
            onSubmit={(e) => {
              e.preventDefault();
              // `handleSubmit` toasts its own failures, so this only fires if
              // that handler itself throws. Log rather than swallow silently.
              handleSubmit(businessId, state?.tree).catch(
                logRejection('Failed to submit allowed features'),
              );
            }}
            className="flex flex-col"
          >
            <div className="p-4 rounded-xl border dark:border-dark-borderMid shadow-sm max-w-md">
              <div className="space-y-1">
                {state?.tree?.map((node) => (
                  <AllowedFeatureNode
                    key={node.key}
                    feature={node}
                    onToggle={handleToggle}
                    disabled={!canUpdate}
                  />
                ))}
              </div>
            </div>

            <PermissionsProvider
              permission={AfriexAdminPermission.BusinessFeaturesUpdate}
            >
              <div className="text-end pb-5">
                <Button
                  dataCy="business-details-allowed-features-submit"
                  disabled={isDisabled}
                  type="submit"
                  variant="solid"
                  colorScheme="cyan"
                >
                  {submitLabel}
                </Button>
              </div>
            </PermissionsProvider>
          </form>
        )}
      </div>
    </React.Fragment>
  );
};
